import React, { useState } from "react";
import { motion } from "framer-motion";
import {
    Phone,
    Mail,
    MapPin,
    DollarSign,
    ShieldCheck,
    Users,
    Sparkles,
    HeartHandshake,
    CalendarCheck,
    Home as HomeIcon,
    ArrowRight,
    Check,
    PawPrint,
    Printer,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import axios from "axios";
import { HOME } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO = "/photos/logo.png";
const BRAND = "Hope Recovery Home";
const TAGLINE = "A Place to Heal. A Path to Grow. A Future to Flourish.";

const HERO_IMG = "/photos/home-10.jpg"; // real front porch "Home Sweet Home"
const STORY_IMG = "/photos/home-7.jpg"; // open kitchen + set dining table
const GALLERY = [
    { src: "/photos/home-3.jpg", alt: "Bright shared bedroom with three beds" },
    { src: "/photos/home-2.jpg", alt: "Restful bedroom with two beds" },
    { src: "/photos/home-8.jpg", alt: "Another shared bedroom with three beds" },
    { src: "/photos/home-6.jpg", alt: "Formal dining room set with care" },
    { src: "/photos/home-1.jpg", alt: "Kitchen with island and dining nook" },
    { src: "/photos/home-11.jpg", alt: "Master bathroom with corner jacuzzi tub" },
    { src: "/photos/home-14.jpg", alt: "Shared bedroom with a wooden cross on the wall" },
    { src: "/photos/home-13.jpg", alt: "Shared bedroom with two twin beds" },
    { src: "/photos/home-12.jpg", alt: "Private bedroom with TV and dresser" },
    { src: "/photos/home-15.jpg", alt: "Another bedroom with two beds" },
    { src: "/photos/home-5.jpg", alt: "Shared bathroom with shower" },
    { src: "/photos/home-9.jpg", alt: "Workspace nook with laundry access" },
];

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const stagger = {
    hidden: {},
    show: { transition: { staggerChildren: 0.08 } },
};

const Nav = () => (
    <header
        data-testid={HOME.nav}
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-[#FAF9F6]/75 border-b border-[var(--hr-line)]"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
            <a
                href="#top"
                data-testid={HOME.navLogo}
                className="flex items-center gap-3 group"
            >
                <img
                    src={LOGO}
                    alt={`${BRAND} logo`}
                    className="h-11 w-11 object-cover rounded-full"
                    style={{ objectPosition: "center 28%" }}
                />
                <span className="font-display text-base sm:text-lg text-[var(--hr-text)] leading-tight">
                    {BRAND}
                </span>
            </a>
            <nav className="hidden md:flex items-center gap-9 text-sm">
                <a
                    href="#story"
                    data-testid={HOME.navStory}
                    className="nav-link"
                >
                    Our Story
                </a>
                <a
                    href="#gallery"
                    data-testid={HOME.navGallery}
                    className="nav-link"
                >
                    The Home
                </a>
                <a
                    href="#requirements"
                    data-testid={HOME.navRequirements}
                    className="nav-link"
                >
                    Move-in
                </a>
                <a
                    href="#contact"
                    data-testid={HOME.navContact}
                    className="nav-link"
                >
                    Contact
                </a>
            </nav>
            <a
                href="#contact"
                data-testid={HOME.navCta}
                className="btn-brand inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium"
            >
                Inquire <ArrowRight className="w-4 h-4" />
            </a>
        </div>
    </header>
);

const Hero = () => (
    <section
        id="top"
        className="relative min-h-screen flex items-center overflow-hidden"
    >
        <img
            src={HERO_IMG}
            alt={`The front porch of ${BRAND} — 'Home Sweet Home'`}
            className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 py-32 w-full text-center">
            <motion.div
                initial="hidden"
                animate="show"
                variants={stagger}
                className="flex flex-col items-center"
            >
                <motion.div
                    variants={fadeUp}
                    className="rounded-3xl bg-[#FAF9F6]/95 backdrop-blur-sm p-3 shadow-[0_30px_80px_-20px_rgba(28,33,27,0.6)] ring-1 ring-[#FAF9F6]/40"
                >
                    <img
                        src={LOGO}
                        alt={`${BRAND} logo`}
                        className="w-44 h-44 sm:w-52 sm:h-52 object-contain"
                    />
                </motion.div>
                <motion.div
                    variants={fadeUp}
                    className="mt-8 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FAF9F6]/15 backdrop-blur-sm border border-[#FAF9F6]/25 text-[#FAF9F6] text-xs tracking-[0.2em] uppercase"
                >
                    <Sparkles className="w-3.5 h-3.5" />
                    Now Open · Welcoming Inquiries
                </motion.div>
                <motion.h1
                    variants={fadeUp}
                    className="font-display text-[#FAF9F6] text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.05]"
                >
                    A new dawn.
                    <br />
                    <span className="italic text-[#F3EFEA]">
                        A fresh start.
                    </span>
                </motion.h1>
                <motion.p
                    variants={fadeUp}
                    className="text-[#F3EFEA]/90 text-base lg:text-lg mt-7 max-w-2xl mx-auto leading-relaxed"
                >
                    {BRAND} is a peer-run transitional
                    living home for men committed to rebuilding their lives
                    after treatment — a safe and supportive place to
                    begin again.
                </motion.p>
                <motion.div
                    variants={fadeUp}
                    className="mt-10 flex flex-wrap items-center justify-center gap-4"
                >
                    <a
                        href="#contact"
                        data-testid={HOME.heroPrimaryCta}
                        className="btn-brand inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium"
                    >
                        Inquire about a bed
                        <ArrowRight className="w-4 h-4" />
                    </a>
                    <a
                        href="#requirements"
                        data-testid={HOME.heroSecondaryCta}
                        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium text-[#FAF9F6] border border-[#FAF9F6]/40 hover:bg-[#FAF9F6]/10 transition-colors"
                    >
                        Move-in requirements
                    </a>
                </motion.div>
                <motion.div
                    variants={fadeUp}
                    className="mt-12 flex flex-wrap items-center justify-center gap-6 text-[#F3EFEA]/80 text-sm"
                >
                    <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" /> 0 beds available
                    </div>
                    <div className="flex items-center gap-2">
                        <HeartHandshake className="w-4 h-4" /> Peer-run
                    </div>
                </motion.div>
            </motion.div>
        </div>
    </section>
);

const Story = () => (
    <section
        id="story"
        data-testid={HOME.storySection}
        className="py-24 lg:py-32"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <motion.div
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="lg:col-span-5 relative"
            >
                <div className="aspect-[4/5] w-full overflow-hidden rounded-2xl border border-[var(--hr-line)] shadow-[0_20px_60px_-30px_rgba(44,53,45,0.35)]">
                    <img
                        src={STORY_IMG}
                        alt="Warm sunny dining room inside the home"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="hidden lg:block absolute -bottom-6 -right-6 w-40 h-40 rounded-2xl bg-[var(--hr-brand)]/15 -z-0" />
            </motion.div>

            <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="lg:col-span-7"
            >
                <div className="flex items-center gap-3 text-[var(--hr-brand)] text-xs tracking-[0.25em] uppercase">
                    <span className="divider-line" /> Our Story
                </div>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.08] mt-5 text-[var(--hr-text)]">
                    A dream God placed
                    <br />
                    <span className="italic text-[var(--hr-text-secondary)]">
                        on our hearts.
                    </span>
                </h2>
                <div className="mt-7 space-y-5 text-[var(--hr-text-secondary)] text-base lg:text-lg leading-relaxed max-w-2xl">
                    <p>
                        My husband and I are both recovering addicts. We made
                        the decision to move out of the house we own and into
                        a rental so we could pursue a dream God placed on our
                        hearts — opening a transitional living home for men
                        seeking a fresh start after treatment.
                    </p>
                    <p>
                        Recovery gave us back our lives, and this home is our
                        way of giving back to our community — walking
                        alongside other recovering addicts as they get back on
                        their feet and work toward independence.
                    </p>
                    <p>
                        Here at <strong className="text-[var(--hr-text)]">{BRAND}</strong> we provide a
                        place to call home for men serious about recovery and
                        rebuilding their life in a safe, supportive, peer-run
                        environment.
                    </p>
                    <p className="text-[var(--hr-text)] font-display text-xl italic">
                        — Kim &amp; Arturo Montana
                    </p>
                </div>
            </motion.div>
        </div>
    </section>
);

const galleryItems = GALLERY.map((g, i) => {
    // bento layout: cycle through varied spans across an 8-image group
    const spans = [
        "lg:col-span-2 lg:row-span-2 aspect-[4/3] lg:aspect-auto", // 0 — large landscape feature
        "lg:row-span-2 aspect-[3/4] lg:aspect-auto",               // 1 — tall portrait
        "aspect-square",                                            // 2 — square
        "aspect-square",                                            // 3 — square
        "lg:col-span-2 aspect-[16/9] lg:aspect-[2/1]",             // 4 — wide
        "lg:row-span-2 aspect-[3/4] lg:aspect-auto",               // 5 — tall portrait
        "aspect-square",                                            // 6 — square
        "aspect-square",                                            // 7 — square
    ];
    return { ...g, className: spans[i % spans.length] };
});

const Gallery = () => (
    <section
        id="gallery"
        data-testid={HOME.gallerySection}
        className="py-24 lg:py-32 bg-[var(--hr-bg-secondary)]"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
                <div>
                    <div className="flex items-center gap-3 text-[var(--hr-brand)] text-xs tracking-[0.25em] uppercase">
                        <span className="divider-line" /> The Home
                    </div>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.25rem] leading-[1.08] mt-5 text-[var(--hr-text)] max-w-2xl">
                        A bright, peaceful place
                        <br className="hidden sm:block" />
                        <span className="italic text-[var(--hr-text-secondary)]"> to call home.</span>
                    </h2>
                </div>
                <p className="text-[var(--hr-text-secondary)] max-w-md leading-relaxed">
                    Comfortable shared living spaces, restful bedrooms, and a
                    community built on accountability and respect.
                </p>
            </div>

            <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={stagger}
                className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5"
            >
                {galleryItems.map((g, i) => (
                    <motion.figure
                        key={i}
                        variants={fadeUp}
                        className={`gallery-tile relative overflow-hidden rounded-2xl border border-[var(--hr-line)] bg-[var(--hr-bg)] ${g.className}`}
                    >
                        <img
                            src={g.src}
                            alt={g.alt}
                            className="gallery-img w-full h-full object-cover"
                            loading="lazy"
                        />
                    </motion.figure>
                ))}
            </motion.div>
        </div>
    </section>
);

const requirements = [
    {
        icon: Users,
        title: "Peer-run environment",
        text: "A community of men supporting one another in accountability and recovery.",
    },
    {
        icon: ShieldCheck,
        title: "100% clean & sober",
        text: "Random drug testing is conducted. A safe, substance-free home for everyone.",
    },
    {
        icon: CalendarCheck,
        title: "2–3 recovery meetings / week",
        text: "Active engagement in your program of recovery is required.",
    },
    {
        icon: HeartHandshake,
        title: "Weekly house meetings",
        text: "Attend mandatory weekly meetings to keep our home strong and connected.",
    },
    {
        icon: HomeIcon,
        title: "Keep the home clean",
        text: "Help maintain shared spaces and clean up after yourself.",
    },
    {
        icon: Check,
        title: "Respect everyone",
        text: "Be respectful of your roommates, the home, and the rules.",
    },
    {
        icon: PawPrint,
        title: "A pet-free home",
        text: "For the comfort and safety of everyone, we keep our home free of pets.",
    },
];

const Requirements = () => (
    <section
        id="requirements"
        data-testid={HOME.requirementsSection}
        className="py-24 lg:py-32"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-14 items-end">
                <div className="lg:col-span-7">
                    <div className="flex items-center gap-3 text-[var(--hr-brand)] text-xs tracking-[0.25em] uppercase">
                        <span className="divider-line" /> Move-in Requirements
                    </div>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.25rem] leading-[1.08] mt-5 text-[var(--hr-text)]">
                        Simple, honest expectations
                        <br />
                        <span className="italic text-[var(--hr-text-secondary)]">for a serious new chapter.</span>
                    </h2>
                </div>
                <div className="lg:col-span-5">
                    <div className="rounded-2xl border border-[var(--hr-line)] bg-[var(--hr-bg-accent)] p-7">
                        <div className="flex items-center gap-3 text-[var(--hr-text-secondary)] text-xs tracking-[0.2em] uppercase">
                            <DollarSign className="w-4 h-4 text-[var(--hr-brand)]" />
                            Pricing
                        </div>
                        <div className="mt-3 flex items-baseline gap-2">
                            <span className="font-display text-5xl text-[var(--hr-text)]">$500</span>
                            <span className="text-[var(--hr-text-secondary)]">/ month rent</span>
                        </div>
                        <div className="mt-2 text-[var(--hr-text-secondary)]">
                            + <strong className="text-[var(--hr-text)]">$100</strong> deposit
                        </div>
                        <p className="mt-4 text-sm text-[var(--hr-text-muted)] leading-relaxed">
                            For move-ins after the 1st, the first month's rent
                            is prorated.
                        </p>
                    </div>
                </div>
            </div>

            <motion.ul
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={stagger}
                className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
            >
                {requirements.map(({ icon: Icon, title, text }, i) => (
                    <motion.li
                        key={i}
                        variants={fadeUp}
                        className="group rounded-2xl border border-[var(--hr-line)] bg-white p-6 hover:border-[var(--hr-brand)] transition-colors"
                    >
                        <div className="w-11 h-11 rounded-full bg-[var(--hr-bg-secondary)] flex items-center justify-center group-hover:bg-[var(--hr-brand)]/15 transition-colors">
                            <Icon className="w-5 h-5 text-[var(--hr-brand)]" />
                        </div>
                        <h3 className="font-display text-xl text-[var(--hr-text)] mt-5">
                            {title}
                        </h3>
                        <p className="text-[var(--hr-text-secondary)] mt-2 leading-relaxed text-sm">
                            {text}
                        </p>
                    </motion.li>
                ))}
            </motion.ul>
        </div>
    </section>
);

const Contact = () => {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const onChange = (e) =>
        setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) {
            toast.error("Please complete name, email, and a short message.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, {
                name: form.name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim() || null,
                message: form.message.trim(),
            });
            setSent(true);
            setForm({ name: "", email: "", phone: "", message: "" });
            toast.success("Thank you — we'll be in touch shortly.");
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                "Something went wrong. Please call us directly.";
            toast.error(detail);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid={HOME.contactSection}
            className="py-24 lg:py-32 bg-[var(--hr-bg-secondary)]"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
                <div className="lg:col-span-5">
                    <div className="flex items-center gap-3 text-[var(--hr-brand)] text-xs tracking-[0.25em] uppercase">
                        <span className="divider-line" /> Contact
                    </div>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-[3rem] leading-[1.1] mt-5 text-[var(--hr-text)]">
                        Reach out.
                        <br />
                        <span className="italic text-[var(--hr-text-secondary)]">We're here.</span>
                    </h2>
                    <p className="mt-5 text-[var(--hr-text-secondary)] leading-relaxed max-w-md">
                        If you or someone you love is ready for a new
                        beginning, give us a call or send us a message — we'd
                        love to talk.
                    </p>

                    <div className="mt-6 rounded-2xl border border-[var(--hr-brand)]/30 bg-[var(--hr-brand)]/10 p-5">
                        <div className="text-xs tracking-[0.22em] uppercase text-[var(--hr-brand-hover)] font-medium">
                            Waitlist Open
                        </div>
                        <p className="mt-2 text-[var(--hr-text)] leading-relaxed text-sm">
                            We are currently full, but please don't hesitate
                            to reach out. We'll take down your information and
                            contact you as soon as a bed becomes available.
                        </p>
                    </div>

                    <div className="mt-10 space-y-4">
                        <a
                            href="tel:+19284506132"
                            data-testid={HOME.contactKimPhone}
                            className="flex items-center gap-4 rounded-2xl border border-[var(--hr-line)] bg-white p-5 hover:border-[var(--hr-brand)] transition-colors"
                        >
                            <span className="w-11 h-11 rounded-full bg-[var(--hr-brand)]/12 flex items-center justify-center">
                                <Phone className="w-5 h-5 text-[var(--hr-brand)]" />
                            </span>
                            <span className="flex-1">
                                <span className="block text-xs tracking-[0.2em] uppercase text-[var(--hr-text-muted)]">
                                    Kim
                                </span>
                                <span className="block font-display text-xl text-[var(--hr-text)]">
                                    (928) 450-6132
                                </span>
                            </span>
                        </a>
                        <a
                            href="tel:+19283036136"
                            data-testid={HOME.contactArturoPhone}
                            className="flex items-center gap-4 rounded-2xl border border-[var(--hr-line)] bg-white p-5 hover:border-[var(--hr-brand)] transition-colors"
                        >
                            <span className="w-11 h-11 rounded-full bg-[var(--hr-brand)]/12 flex items-center justify-center">
                                <Phone className="w-5 h-5 text-[var(--hr-brand)]" />
                            </span>
                            <span className="flex-1">
                                <span className="block text-xs tracking-[0.2em] uppercase text-[var(--hr-text-muted)]">
                                    Arturo
                                </span>
                                <span className="block font-display text-xl text-[var(--hr-text)]">
                                    (928) 303-6136
                                </span>
                            </span>
                        </a>
                        <a
                            href="mailto:transitionalliving@hoperecoveryhomellc.com"
                            data-testid={HOME.contactEmail}
                            className="flex items-center gap-4 rounded-2xl border border-[var(--hr-line)] bg-white p-5 hover:border-[var(--hr-brand)] transition-colors"
                        >
                            <span className="w-11 h-11 rounded-full bg-[var(--hr-brand)]/12 flex items-center justify-center">
                                <Mail className="w-5 h-5 text-[var(--hr-brand)]" />
                            </span>
                            <span className="flex-1 min-w-0">
                                <span className="block text-xs tracking-[0.2em] uppercase text-[var(--hr-text-muted)]">
                                    Email
                                </span>
                                <span className="block font-display text-base sm:text-lg text-[var(--hr-text)] truncate">
                                    transitionalliving@hoperecoveryhomellc.com
                                </span>
                            </span>
                        </a>
                    </div>
                </div>

                <div className="lg:col-span-7">
                    <form
                        onSubmit={onSubmit}
                        data-testid={HOME.contactForm}
                        className="rounded-2xl border border-[var(--hr-line)] bg-[var(--hr-bg)] p-7 lg:p-10"
                    >
                        <h3 className="font-display text-2xl text-[var(--hr-text)]">
                            Send us a message
                        </h3>
                        <p className="text-[var(--hr-text-muted)] text-sm mt-1">
                            We'll get back to you as soon as we can.
                        </p>

                        <div className="grid sm:grid-cols-2 gap-4 mt-7">
                            <label className="block">
                                <span className="text-xs tracking-[0.18em] uppercase text-[var(--hr-text-muted)]">
                                    Your name
                                </span>
                                <input
                                    name="name"
                                    type="text"
                                    required
                                    value={form.name}
                                    onChange={onChange}
                                    data-testid={HOME.contactName}
                                    className="input-field mt-2 w-full px-4 py-3 rounded-lg text-sm"
                                    placeholder="Full name"
                                />
                            </label>
                            <label className="block">
                                <span className="text-xs tracking-[0.18em] uppercase text-[var(--hr-text-muted)]">
                                    Email
                                </span>
                                <input
                                    name="email"
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={onChange}
                                    data-testid={HOME.contactEmailInput}
                                    className="input-field mt-2 w-full px-4 py-3 rounded-lg text-sm"
                                    placeholder="you@example.com"
                                />
                            </label>
                            <label className="block sm:col-span-2">
                                <span className="text-xs tracking-[0.18em] uppercase text-[var(--hr-text-muted)]">
                                    Phone (optional)
                                </span>
                                <input
                                    name="phone"
                                    type="tel"
                                    value={form.phone}
                                    onChange={onChange}
                                    data-testid={HOME.contactPhoneInput}
                                    className="input-field mt-2 w-full px-4 py-3 rounded-lg text-sm"
                                    placeholder="(555) 123-4567"
                                />
                            </label>
                            <label className="block sm:col-span-2">
                                <span className="text-xs tracking-[0.18em] uppercase text-[var(--hr-text-muted)]">
                                    Message
                                </span>
                                <textarea
                                    name="message"
                                    rows={6}
                                    required
                                    value={form.message}
                                    onChange={onChange}
                                    data-testid={HOME.contactMessage}
                                    className="input-field mt-2 w-full px-4 py-3 rounded-lg text-sm resize-y"
                                    placeholder="Tell us a little about yourself or who you're inquiring for…"
                                />
                            </label>
                        </div>

                        <div className="mt-7 flex flex-wrap items-center justify-between gap-4">
                            <p className="text-xs text-[var(--hr-text-muted)] max-w-sm">
                                By submitting, you agree to be contacted by
                                Hope Recovery Home at the info above.
                            </p>
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid={HOME.contactSubmit}
                                className="btn-brand inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {submitting ? "Sending…" : "Send message"}
                                {!submitting && <ArrowRight className="w-4 h-4" />}
                            </button>
                        </div>

                        {sent && (
                            <div
                                data-testid={HOME.contactSuccess}
                                className="mt-6 rounded-lg border border-[var(--hr-accent)]/60 bg-[var(--hr-accent)]/10 text-[var(--hr-text-secondary)] px-4 py-3 text-sm"
                            >
                                Thank you for reaching out. Kim or Arturo will
                                respond as soon as possible.
                            </div>
                        )}
                    </form>
                </div>
            </div>
            <Toaster
                position="bottom-right"
                toastOptions={{
                    style: {
                        background: "#FAF9F6",
                        color: "#2C352D",
                        border: "1px solid #DDD5C8",
                    },
                }}
            />
        </section>
    );
};

const Footer = () => (
    <footer
        data-testid={HOME.footer}
        className="py-12 bg-[#2C352D] text-[#F3EFEA] relative overflow-hidden grain"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-8 items-start">
            <div>
                <div className="flex items-center gap-3">
                    <span className="w-11 h-11 rounded-full bg-[#FAF9F6] overflow-hidden flex items-center justify-center">
                        <img
                            src={LOGO}
                            alt={`${BRAND} logo`}
                            className="w-11 h-11 object-cover"
                            style={{ objectPosition: "center 28%" }}
                        />
                    </span>
                    <div className="leading-tight">
                        <div className="font-display text-base">
                            {BRAND}
                        </div>
                        <div className="text-[10px] tracking-[0.22em] uppercase text-[#F3EFEA]/55 mt-0.5">
                            Transitional Living Home
                        </div>
                    </div>
                </div>
                <p className="mt-4 font-display italic text-sm text-[#F3EFEA]/80">
                    {TAGLINE}
                </p>
                <p className="mt-3 text-sm text-[#F3EFEA]/65 max-w-sm leading-relaxed">
                    A peer-run transitional living home for men rebuilding
                    their lives in recovery.
                </p>
            </div>
            <div className="text-sm space-y-2">
                <div className="text-xs tracking-[0.25em] uppercase text-[#F3EFEA]/60 mb-2">
                    Call us
                </div>
                <a href="tel:+19284506132" className="block hover:text-[var(--hr-brand)] transition-colors">
                    Kim — (928) 450-6132
                </a>
                <a href="tel:+19283036136" className="block hover:text-[var(--hr-brand)] transition-colors">
                    Arturo — (928) 303-6136
                </a>
            </div>
            <div className="text-sm space-y-2">
                <div className="text-xs tracking-[0.25em] uppercase text-[#F3EFEA]/60 mb-2">
                    Email
                </div>
                <a
                    href="mailto:transitionalliving@hoperecoveryhomellc.com"
                    className="break-all hover:text-[var(--hr-brand)] transition-colors"
                >
                    transitionalliving@hoperecoveryhomellc.com
                </a>
            </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-10 pt-6 border-t border-[#F3EFEA]/10 text-xs text-[#F3EFEA]/50 flex flex-wrap items-center justify-between gap-3">
            <span>© {new Date().getFullYear()} {BRAND} LLC. All rights reserved.</span>
            <span className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5" /> Arizona
            </span>
        </div>
    </footer>
);

const HopeRecoveryHome = () => (
    <div className="App">
        <Nav />
        <Hero />
        <Story />
        <Gallery />
        <Requirements />
        <Contact />
        <Footer />
    </div>
);

export default HopeRecoveryHome;
